import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  Button,
  Divider,
  Tabs,
  Tab,
  Chip,
  Stack,
  Alert
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Gavel as GavelIcon,
  Security as SecurityIcon,
  Dns as ServerIcon,
  CheckCircle as CheckIcon,
  VerifiedUser as ShieldIcon
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import NebenanHilfeLogo from '../UI/NebenanHilfeLogo';
import { updatePageSEO } from '../../utils/seo';

const LegalPage = ({ type: propType }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const initialType = propType || (location.pathname.includes('datenschutz') || location.pathname.includes('privacy') ? 'privacy' : 'agb');
  const [currentTab, setCurrentTab] = useState(initialType);

  useEffect(() => {
    if (currentTab === 'agb') {
      updatePageSEO(
        'Allgemeine Geschäftsbedingungen (AGB)',
        'Allgemeine Geschäftsbedingungen für die Nutzung der Plattform NebenanHilfe.de. 100% kostenfrei, gemeinnützig und mit Serverstandort Deutschland.'
      );
    } else {
      updatePageSEO(
        'Datenschutzerklärung (DSGVO)',
        'Datenschutzerklärung für NebenanHilfe.de. Schutz personenbezogener Daten auf zertifizierten ISO 27001 Servern in Deutschland (DSGVO & BDSG).'
      );
    }
  }, [currentTab]);

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f8fafc', py: { xs: 3, md: 6 } }}>
      <Container maxWidth="md">
        {/* Navigation & Logo */}
        <Box sx={{ mb: 4, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate(-1)}
            variant="outlined"
            size="small"
            sx={{ fontWeight: 700, textTransform: 'none', borderRadius: 2 }}
          >
            Zurück
          </Button>
          <NebenanHilfeLogo size="medium" />
        </Box>

        {/* Main Content Card */}
        <Paper
          elevation={0}
          sx={{
            p: { xs: 3, sm: 4, md: 5 },
            borderRadius: 4,
            border: '1px solid rgba(0,0,0,0.08)',
            boxShadow: '0 4px 24px rgba(0,0,0,0.04)'
          }}
        >
          {/* Header Title & Badges */}
          <Box sx={{ mb: 3 }}>
            <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ gap: 1, mb: 2 }}>
              <Chip
                icon={<ServerIcon sx={{ color: '#059669 !important', fontSize: 16 }} />}
                label="🇩🇪 Serverstandort Deutschland"
                sx={{ bgcolor: 'rgba(16, 185, 129, 0.1)', color: '#047857', fontWeight: 700, border: '1px solid rgba(16, 185, 129, 0.2)' }}
              />
              <Chip
                icon={<ShieldIcon sx={{ color: '#2563eb !important', fontSize: 16 }} />}
                label="100% DSGVO-konform"
                sx={{ bgcolor: 'rgba(37, 99, 235, 0.1)', color: '#1d4ed8', fontWeight: 700, border: '1px solid rgba(37, 99, 235, 0.2)' }}
              />
              <Chip
                icon={<CheckIcon sx={{ color: '#0d9488 !important', fontSize: 16 }} />}
                label="100% Kostenfrei"
                sx={{ bgcolor: 'rgba(13, 148, 136, 0.1)', color: '#0f766e', fontWeight: 700, border: '1px solid rgba(13, 148, 136, 0.2)' }}
              />
            </Stack>

            <Typography variant="h4" component="h1" fontWeight={800} sx={{ color: 'text.primary', letterSpacing: '-0.5px' }}>
              Rechtliche Hinweise & Datenschutz
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              NebenanHilfe.de • Plattform für nachbarschaftliche Hilfe & Gemeinschaft • Stand: 2026
            </Typography>
          </Box>

          {/* Tab Switcher */}
          <Tabs
            value={currentTab}
            onChange={(e, val) => setCurrentTab(val)}
            sx={{
              mb: 4,
              borderBottom: 1,
              borderColor: 'divider',
              '& .MuiTab-root': { fontWeight: 700, textTransform: 'none', fontSize: '1rem', minHeight: 48 }
            }}
          >
            <Tab
              value="agb"
              icon={<GavelIcon sx={{ mr: 1, fontSize: 20 }} />}
              iconPosition="start"
              label="Allgemeine Geschäftsbedingungen (AGB)"
            />
            <Tab
              value="privacy"
              icon={<SecurityIcon sx={{ mr: 1, fontSize: 20 }} />}
              iconPosition="start"
              label="Datenschutzerklärung (DSGVO)"
            />
          </Tabs>

          {/* Highlight Box: Server in Deutschland */}
          <Alert
            icon={<ServerIcon fontSize="inherit" />}
            severity="success"
            sx={{
              mb: 4,
              borderRadius: 3,
              border: '1px solid rgba(16, 185, 129, 0.3)',
              bgcolor: 'rgba(16, 185, 129, 0.05)',
              color: '#064e3b',
              '& .MuiAlert-icon': { color: '#059669' }
            }}
          >
            <Typography variant="subtitle2" fontWeight={800} gutterBottom>
              Garantierter Serverstandort in Deutschland
            </Typography>
            <Typography variant="body2" sx={{ lineHeight: 1.6 }}>
              Alle Datenbanken, Benutzerkonten, Chats, Medien und Beiträge von <strong>NebenanHilfe.de</strong> werden ausschließlich in nach <strong>ISO 27001 zertifizierten Hochsicherheits-Rechenzentren in der Bundesrepublik Deutschland</strong> (u. a. Hetzner Nürnberg & Falkenstein) gespeichert und verarbeitet. Deine Daten unterliegen strengstem deutschen und europäischen Datenschutzrecht (DSGVO / BDSG).
            </Typography>
          </Alert>

          {currentTab === 'agb' ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3.5 }}>
              <Box>
                <Typography variant="h6" fontWeight={800} gutterBottom sx={{ color: 'primary.main', display: 'flex', alignItems: 'center', gap: 1 }}>
                  1. Geltungsbereich & Vertragsgegenstand
                </Typography>
                <Typography variant="body1" sx={{ lineHeight: 1.8, color: 'text.secondary' }}>
                  Diese Allgemeinen Geschäftsbedingungen gelten für die Registrierung und Nutzung der Nachbarschaftsplattform <strong>NebenanHilfe.de</strong>. Zweck der Plattform ist die Förderung von nachbarschaftlicher Hilfe, uneigennütziger Unterstützung im Alltag, lokalem Leihen und Tauschen sowie dem Austausch in Quartiersforen auf Basis von gegenseitigem Respekt und Vertrauen.
                </Typography>
              </Box>

              <Divider />

              <Box>
                <Typography variant="h6" fontWeight={800} gutterBottom sx={{ color: 'primary.main', display: 'flex', alignItems: 'center', gap: 1 }}>
                  2. 100% Kostenfreiheit & Gemeinnütziger Charakter
                </Typography>
                <Typography variant="body1" sx={{ lineHeight: 1.8, color: 'text.secondary' }}>
                  • <strong>Vollständig kostenfrei</strong>: Die Nutzung der gesamten Plattform NebenanHilfe.de ist für alle Bürgerinnen und Bürger zu 100% kostenlos.<br />
                  • <strong>Keine Abonnements oder versteckte Kosten</strong>: Es gibt weder Registrierungsgebühren noch Mitgliedsbeiträge, Werbeverträge oder Abomodelle.<br />
                  • <strong>Nachbarschaftshilfe</strong>: Nachbarn unterstützen sich gegenseitig freiwillig, unentgeltlich und im Sinne christlicher Nächstenliebe.
                </Typography>
              </Box>

              <Divider />

              <Box>
                <Typography variant="h6" fontWeight={800} gutterBottom sx={{ color: 'primary.main', display: 'flex', alignItems: 'center', gap: 1 }}>
                  3. Datensouveränität & Serverstandort Deutschland
                </Typography>
                <Typography variant="body1" sx={{ lineHeight: 1.8, color: 'text.secondary' }}>
                  Zur Gewährleistung höchster digitaler Souveränität und Sicherheit betreibt NebenanHilfe.de sämtliche Serverinfrastrukturen ausschließlich an <strong>deutschen Rechenzentrumsstandorten</strong>. Daten werden niemals an unbeteiligte Dritte verkauft oder zweckentfremdet.
                </Typography>
              </Box>

              <Divider />

              <Box>
                <Typography variant="h6" fontWeight={800} gutterBottom sx={{ color: 'primary.main', display: 'flex', alignItems: 'center', gap: 1 }}>
                  4. Verhaltenskodex & Nutzerpflichten
                </Typography>
                <Typography variant="body1" sx={{ lineHeight: 1.8, color: 'text.secondary' }}>
                  Alle Teilnehmer verpflichten sich zu wahrheitsgemäßen Profilangaben, einem höflichen, respektvollen Miteinander und der Beachtung geltender Gesetze. Beleidigungen, Diskriminierung, Belästigung, Spam sowie jeglicher gewerblich-kommerzieller Missbrauch sind strikt untersagt und führen zur sofortigen Sperrung.
                </Typography>
              </Box>

              <Divider />

              <Box>
                <Typography variant="h6" fontWeight={800} gutterBottom sx={{ color: 'primary.main', display: 'flex', alignItems: 'center', gap: 1 }}>
                  5. Haftungsausschluss & Eigenverantwortung
                </Typography>
                <Typography variant="body1" sx={{ lineHeight: 1.8, color: 'text.secondary' }}>
                  NebenanHilfe.de stellt ausschließlich die digitale Kommunikationsinfrastruktur zur Verfügung. Für Vereinbarungen, Absprachen, Nachbarschaftshilfen oder Leihgeschäfte, die zwischen Nutzern zustande kommen, übernimmt NebenanHilfe.de keine Haftung. Jeder Nutzer handelt eigenverantwortlich.
                </Typography>
              </Box>
            </Box>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3.5 }}>
              <Box>
                <Typography variant="h6" fontWeight={800} gutterBottom sx={{ color: 'primary.main', display: 'flex', alignItems: 'center', gap: 1 }}>
                  1. Verantwortliche Stelle im Sinne der DSGVO
                </Typography>
                <Typography variant="body1" sx={{ lineHeight: 1.8, color: 'text.secondary' }}>
                  Verantwortlich für die Datenverarbeitung auf dieser Plattform ist das Betreiberteam von <strong>NebenanHilfe.de</strong>.<br />
                  Kontakt: <strong>support@nebenanhilfe.de</strong>
                </Typography>
              </Box>

              <Divider />

              <Box>
                <Typography variant="h6" fontWeight={800} gutterBottom sx={{ color: 'primary.main', display: 'flex', alignItems: 'center', gap: 1 }}>
                  2. Speicherung auf Servern in Deutschland (Art. 32 DSGVO)
                </Typography>
                <Typography variant="body1" sx={{ lineHeight: 1.8, color: 'text.secondary' }}>
                  Der Schutz deiner Privatsphäre hat oberste Priorität. Sämtliche personenbezogenen Daten (Benutzerstammdaten, Postleitzahlen, verschlüsselte Passwörter, Chat-Nachrichten, Medien und Foreneinträge) werden ausschließlich auf Servern in <strong>Deutschland (Standort Nürnberg / Falkenstein)</strong> nach strengsten technischen und organisatorischen Maßnahmen (TOM) gemäß Art. 32 DSGVO verarbeitet. Es erfolgt keine Übermittlung in unsichere Drittstaaten.
                </Typography>
              </Box>

              <Divider />

              <Box>
                <Typography variant="h6" fontWeight={800} gutterBottom sx={{ color: 'primary.main', display: 'flex', alignItems: 'center', gap: 1 }}>
                  3. Zweckbindung der Datenverarbeitung
                </Typography>
                <Typography variant="body1" sx={{ lineHeight: 1.8, color: 'text.secondary' }}>
                  Wir erheben und verarbeiten Daten ausschließlich für:<br />
                  • Die Bereitstellung der lokalen Nachbarschaftshilfe und des Quartiersnetzwerks<br />
                  • Den Schutz des Kontos durch sichere 2FA (Zwei-Faktor-Authentifizierung & TOTP Authenticator)<br />
                  • Die sichere Echtzeit-Kommunikation und den Schutz vor Missbrauch<br />
                  Es erfolgt kein Tracking zu Werbezwecken und keine Weitergabe an Werbetreibende.
                </Typography>
              </Box>

              <Divider />

              <Box>
                <Typography variant="h6" fontWeight={800} gutterBottom sx={{ color: 'primary.main', display: 'flex', alignItems: 'center', gap: 1 }}>
                  4. Deine Rechte als betroffene Person (Art. 15–21 DSGVO)
                </Typography>
                <Typography variant="body1" sx={{ lineHeight: 1.8, color: 'text.secondary' }}>
                  Als Nutzer hast du jederzeit das Recht auf:<br />
                  • <strong>Auskunft</strong> über deine gespeicherten Daten (Art. 15 DSGVO)<br />
                  • <strong>Berichtigung</strong> unrichtiger Daten (Art. 16 DSGVO)<br />
                  • <strong>Vollständige Löschung</strong> deines Kontos und aller Daten direkt im Profil (Art. 17 DSGVO)<br />
                  • <strong>Einschränkung der Verarbeitung</strong> sowie <strong>Datenübertragbarkeit</strong> (Art. 18 & 20 DSGVO)
                </Typography>
              </Box>
            </Box>
          )}
        </Paper>
      </Container>
    </Box>
  );
};

export default LegalPage;
